import React, { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AddRounded,
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  AutoAwesomeOutlined,
  DeleteOutlineRounded,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingFieldOption,
  DocumentCraftingFormField,
  DocumentCraftingUpload,
} from "../../../Models/documentCrafting";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { localizedText } from "../Common/useDocumentCraftingEnumLabels";
import {
  FormError,
  FormErrors,
  FormValues,
  PendingFile,
  PendingFiles,
  asRecord,
  emptyListItem,
  fieldDomId,
  isEmptyValue,
  isBlankRow,
  isFieldRequired,
  joinPath,
  limitedItemFields,
  wordCount,
} from "./documentFormModel";
import UploadField from "./UploadField";

export interface SchemaFormProps {
  /** The fields to show (e.g. one wizard step); nested fields come along. */
  fields: DocumentCraftingFormField[];
  /** All values of the form (paths and `required_if` resolve from here). */
  values: FormValues;
  errors: FormErrors;
  onChange: (path: string, value: unknown) => void;
  /** New files per file field path. */
  files: PendingFiles;
  onFilesChange: (
    path: string,
    update: (current: PendingFile[]) => PendingFile[],
  ) => void;
  /** Earlier uploads per file field path (duplicate, revise); a file field's
   * value holds the ticked ones. */
  uploads?: Record<string, DocumentCraftingUpload[]>;
  uploadsDocumentId?: number | null;
  workspaceMaxMb?: number | null;
  /** Edited alt texts of earlier uploads (revise form). */
  uploadAlts?: Record<number, string>;
  onUploadAltChange?: (uploadId: number, alt: string) => void;
  disabled?: boolean;
  /** Field to mark, e.g. the one a quality note points at (`?field=`). */
  highlightPath?: string | null;
  /** All fields of the type, when `fields` is only a part of the form:
   * resolves the targets of `required_if`. Defaults to `fields`. */
  schema?: DocumentCraftingFormField[];
}

type Context = Omit<SchemaFormProps, "fields" | "schema"> & { schema: DocumentCraftingFormField[] };

const SchemaFormContext = createContext<Context | null>(null);

const useSchemaForm = () => {
  const context = useContext(SchemaFormContext);
  if (!context) throw new Error("SchemaForm fields need a SchemaForm");
  return context;
};

/** Text of a field error: the server's message or the translated rule. */
export const useFormErrorText = () => {
  const { t } = useTranslation("documentCrafting");
  return (error: FormError | null | undefined): string | undefined => {
    if (!error) return undefined;
    if (error.message) return error.message;
    return t(`create.errors.${error.key}`, error.params ?? {});
  };
};

/**
 * Scrolls to a field (or the closest enclosing one, e.g. the list of a row
 * that does not exist) and focuses its first input. False if none is shown.
 */
export const focusFormField = (path: string): boolean => {
  const parts = path.split(".");
  for (let length = parts.length; length > 0; length -= 1) {
    const element = document.getElementById(fieldDomId(parts.slice(0, length).join(".")));
    if (element) {
      element.scrollIntoView?.({ block: "center", behavior: "smooth" });
      element
        .querySelector<HTMLElement>(
          "input:not([type=hidden]):not([type=file]):not([disabled]), textarea:not([disabled]), [tabindex='0']",
        )
        ?.focus({ preventScroll: true });
      return true;
    }
  }
  return false;
};

const FULL_WIDTH_TYPES = ["textarea", "list", "group", "file", "multiselect", "boolean"];

const FieldGrid: React.FC<{
  fields: DocumentCraftingFormField[];
  prefix: string;
  scope: Record<string, unknown>;
}> = ({ fields, prefix, scope }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
      gap: 2,
    }}
  >
    {fields.map((field) => (
      <FieldRenderer key={field.key} field={field} prefix={prefix} scope={scope} />
    ))}
  </Box>
);

const AiHint: React.FC = () => {
  const { t } = useTranslation("documentCrafting");
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <AutoAwesomeOutlined sx={{ fontSize: 14 }} />
      {t("create.form.aiFillsIfEmpty")}
    </Box>
  );
};

/** Helper line: the error, else help and AI hint; a counter on the right. */
const Helper: React.FC<{
  error?: string;
  help?: string;
  aiHint?: boolean;
  counter?: string;
}> = ({ error, help, aiHint, counter }) => {
  if (!error && !help && !aiHint && !counter) return null;
  return (
    <Box component="span" sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}>
      <Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {error ? (
          <span>{error}</span>
        ) : (
          <>
            {help && <span>{help}</span>}
            {aiHint && <AiHint />}
          </>
        )}
      </Box>
      {counter && (
        <Box component="span" sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
          {counter}
        </Box>
      )}
    </Box>
  );
};

const optionLabel = (option: DocumentCraftingFieldOption, language: string) =>
  localizedText(option.label, language) || option.value;

const FieldRenderer: React.FC<{
  field: DocumentCraftingFormField;
  prefix: string;
  scope: Record<string, unknown>;
}> = ({ field, prefix, scope }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const form = useSchemaForm();
  const errorText = useFormErrorText();
  const { accentSoft, accentBorder } = useDashboardColors();
  const language = i18n.language;

  const path = joinPath(prefix, field.key);
  const value = scope[field.key];
  const error = errorText(form.errors[path]);
  const required = isFieldRequired(field, scope, {
    root: form.values,
    files: form.files,
    fields: form.schema,
  });
  const label = localizedText(field.label, language) || field.key;
  const help = localizedText(field.help, language) || undefined;
  const placeholder = localizedText(field.placeholder, language) || undefined;
  const highlighted = !!form.highlightPath && form.highlightPath === path;
  const disabled = form.disabled;

  const wrapperSx = {
    gridColumn: FULL_WIDTH_TYPES.includes(field.type) ? "1 / -1" : undefined,
    minWidth: 0,
    ...(highlighted
      ? {
          p: 1,
          m: -1,
          borderRadius: "10px",
          backgroundColor: accentSoft,
          boxShadow: `0 0 0 2px ${accentBorder}`,
        }
      : {}),
  };

  const renderInput = () => {
    switch (field.type) {
      case "group":
        return <GroupField field={field} path={path} value={value} required={required} />;
      case "list":
        return <ListField field={field} path={path} value={value} required={required} />;
      case "file":
        return (
          <UploadField
            field={field}
            id={`${fieldDomId(path)}-drop`}
            label={label}
            required={required}
            help={help}
            files={form.files[path] ?? []}
            onFilesChange={(update) => form.onFilesChange(path, update)}
            existing={form.uploads?.[path] ?? []}
            existingDocumentId={form.uploadsDocumentId}
            selectedIds={Array.isArray(value) ? (value as number[]) : []}
            onSelectedIdsChange={(ids) => form.onChange(path, ids)}
            workspaceMaxMb={form.workspaceMaxMb}
            existingAlts={form.uploadAlts}
            onExistingAltChange={form.onUploadAltChange}
            error={error}
            disabled={disabled}
          />
        );
      case "boolean":
        return (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === true}
                  disabled={disabled}
                  onChange={(event) => form.onChange(path, event.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: 14, color: color.gray900 }}>
                    {label}
                    {required && (
                      <Box component="span" sx={{ color: color.red600 }}>
                        {" *"}
                      </Box>
                    )}
                  </Typography>
                  {help && (
                    <Typography sx={{ fontSize: 12, color: color.gray500 }}>{help}</Typography>
                  )}
                </Box>
              }
              sx={{
                m: 0,
                pr: 1.5,
                py: 0.5,
                width: "100%",
                alignItems: "flex-start",
                borderRadius: "9px",
                border: `1px solid ${error ? color.red600 : value === true ? color.gray400 : color.gray200}`,
                "& .MuiCheckbox-root": { pt: 0.5 },
              }}
            />
            {error && <FormHelperText error>{error}</FormHelperText>}
          </Box>
        );
      case "multiselect":
        return <MultiSelectField field={field} path={path} value={value} required={required} />;
      case "select":
        if (field.allow_custom) {
          const options = field.options ?? [];
          const text = typeof value === "string" ? value : "";
          const selected = options.find((option) => option.value === text) ?? null;
          return (
            <Autocomplete<DocumentCraftingFieldOption, false, false, true>
              freeSolo
              size="small"
              options={options}
              disabled={disabled}
              value={selected ?? (text || null)}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : optionLabel(option, language)
              }
              isOptionEqualToValue={(option, current) =>
                typeof current !== "string" && option.value === current.value
              }
              onChange={(_, next) =>
                form.onChange(path, typeof next === "string" ? next : (next?.value ?? null))
              }
              onInputChange={(_, next, reason) => {
                if (reason !== "input") return;
                const match = options.find((option) => optionLabel(option, language) === next);
                form.onChange(path, match ? match.value : next);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  required={required}
                  placeholder={placeholder}
                  error={!!error}
                  helperText={<Helper error={error} help={help} aiHint={field.ai_fills_if_empty} />}
                />
              )}
            />
          );
        }
        return (
          <TextField
            select
            fullWidth
            size="small"
            label={label}
            required={required}
            disabled={disabled}
            value={typeof value === "string" ? value : ""}
            onChange={(event) =>
              form.onChange(path, event.target.value === "" ? null : event.target.value)
            }
            error={!!error}
            helperText={<Helper error={error} help={help} aiHint={field.ai_fills_if_empty} />}
          >
            {!required && <MenuItem value="">{t("create.form.notSpecified")}</MenuItem>}
            {(field.options ?? []).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {optionLabel(option, language)}
              </MenuItem>
            ))}
          </TextField>
        );
      case "number":
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            label={label}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            value={typeof value === "number" && Number.isFinite(value) ? value : ""}
            onChange={(event) =>
              form.onChange(path, event.target.value === "" ? null : Number(event.target.value))
            }
            inputProps={{ min: field.min ?? undefined, max: field.max ?? undefined, step: 1 }}
            error={!!error}
            helperText={<Helper error={error} help={help} aiHint={field.ai_fills_if_empty} />}
          />
        );
      default: {
        const text = typeof value === "string" ? value : value === null || value === undefined ? "" : String(value);
        const multiline = field.type === "textarea" || field.format === "chart_table";
        const counters: string[] = [];
        if (field.type === "textarea" && field.max_length) {
          counters.push(
            field.min_length && text.trim().length < field.min_length
              ? t("create.form.counterMin", { length: text.length, max: field.max_length, min: field.min_length })
              : t("create.form.counter", { length: text.length, max: field.max_length }),
          );
        }
        if (field.max_words) {
          counters.push(t("create.form.wordCounter", { count: wordCount(text), max: field.max_words }));
        }
        const inputType =
          field.format === "phone"
            ? "tel"
            : field.type === "url" || field.type === "email" || field.type === "date" || field.type === "time"
              ? field.type
              : "text";
        const formatHelp =
          field.format === "chart_table"
            ? t("create.form.chartTableHelp")
            : field.format === "url_or_email"
              ? t("create.form.urlOrEmailHelp")
              : undefined;
        return (
          <TextField
            fullWidth
            size="small"
            type={inputType}
            label={label}
            required={required}
            disabled={disabled}
            placeholder={placeholder ?? (field.type === "url" ? "https://" : undefined)}
            value={text}
            onChange={(event) => form.onChange(path, event.target.value)}
            multiline={multiline}
            minRows={multiline ? (field.format === "chart_table" ? 5 : 3) : undefined}
            maxRows={multiline ? 14 : undefined}
            InputLabelProps={field.type === "date" || field.type === "time" ? { shrink: true } : undefined}
            inputProps={{
              maxLength: field.max_length ?? undefined,
              ...(field.format === "chart_table"
                ? { style: { fontFamily: "monospace", fontSize: 13 } }
                : {}),
            }}
            error={!!error}
            helperText={
              <Helper
                error={error}
                help={[help, formatHelp].filter(Boolean).join(" ") || undefined}
                aiHint={field.ai_fills_if_empty}
                counter={counters.join(" · ") || undefined}
              />
            }
          />
        );
      }
    }
  };

  return (
    <Box id={fieldDomId(path)} sx={wrapperSx}>
      {renderInput()}
    </Box>
  );
};

const MultiSelectField: React.FC<{
  field: DocumentCraftingFormField;
  path: string;
  value: unknown;
  required: boolean;
}> = ({ field, path, value, required }) => {
  const { i18n } = useTranslation("documentCrafting");
  const form = useSchemaForm();
  const errorText = useFormErrorText();
  const selected = Array.isArray(value) ? value.map(String) : [];
  const error = errorText(form.errors[path]);
  const label = localizedText(field.label, i18n.language) || field.key;
  const help = localizedText(field.help, i18n.language);

  const toggle = (option: string) =>
    form.onChange(
      path,
      selected.includes(option)
        ? selected.filter((item) => item !== option)
        : [...selected, option],
    );

  return (
    <Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: color.gray900 }}>
        {label}
        {required && (
          <Box component="span" sx={{ color: color.red600 }}>
            {" *"}
          </Box>
        )}
      </Typography>
      {help && <Typography sx={{ fontSize: 12, color: color.gray500 }}>{help}</Typography>}
      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 1,
        }}
      >
        {(field.options ?? []).map((option) => {
          const checked = selected.includes(option.value);
          return (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={checked}
                  disabled={form.disabled}
                  onChange={() => toggle(option.value)}
                />
              }
              label={optionLabel(option, i18n.language)}
              sx={{
                m: 0,
                pr: 1,
                borderRadius: "9px",
                border: `1px solid ${checked ? color.gray400 : color.gray200}`,
                "& .MuiFormControlLabel-label": { fontSize: 14 },
              }}
            />
          );
        })}
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

const FieldLabel: React.FC<{ label: string; required: boolean; aside?: string }> = ({
  label,
  required,
  aside,
}) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: color.gray900 }}>
      {label}
      {required && (
        <Box component="span" sx={{ color: color.red600 }}>
          {" *"}
        </Box>
      )}
    </Typography>
    {aside && <Typography sx={{ fontSize: 12, color: color.gray500 }}>{aside}</Typography>}
  </Box>
);

const ListField: React.FC<{
  field: DocumentCraftingFormField;
  path: string;
  value: unknown;
  required: boolean;
}> = ({ field, path, value, required }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const form = useSchemaForm();
  const errorText = useFormErrorText();
  const items = Array.isArray(value) ? value : [];
  const label = localizedText(field.label, i18n.language) || field.key;
  const help = localizedText(field.help, i18n.language);
  const placeholder = localizedText(field.placeholder, i18n.language) || undefined;
  const error = errorText(form.errors[path]);
  const max = field.max_items ?? null;
  const min = field.min_items ?? null;
  const keepRows = required ? Math.max(1, min ?? 1) : 0;
  const canAdd = !form.disabled && (max === null || items.length < max);

  const allowed = field.allowed_counts ?? [];
  const aside =
    allowed.length > 1
      ? t("create.form.listAllowed", {
          list: allowed.slice(0, -1).join(", "),
          last: allowed[allowed.length - 1],
        })
      : min !== null && max !== null && min === max
      ? t("create.form.listExactly", { count: min })
      : min !== null && max !== null
        ? t("create.form.listRange", { min, max })
        : max !== null
          ? t("create.form.listMax", { count: max })
          : undefined;

  const update = (next: unknown[]) => form.onChange(path, next);
  // Row limits follow the number of filled rows (e.g. three price cards are
  // narrower than two).
  const entryCount = items.filter(
    (item, index) => !isBlankRow(field, item, joinPath(path, index), form.files),
  ).length;
  const itemFields = limitedItemFields(field, entryCount);
  const exclusiveKeys = itemFields.filter((item) => item.exclusive).map((item) => item.key);
  // Ticking an "only one row" box unticks it in the other rows.
  const rowContext: Context = exclusiveKeys.length
    ? {
        ...form,
        onChange: (changedPath, next) => {
          const match = changedPath.slice(path.length + 1).match(/^(\d+)\.([^.]+)$/);
          if (
            changedPath.startsWith(`${path}.`) &&
            match &&
            next === true &&
            exclusiveKeys.includes(match[2])
          ) {
            const row = Number(match[1]);
            update(
              items.map((item, index) => ({ ...asRecord(item), [match[2]]: index === row })),
            );
            return;
          }
          form.onChange(changedPath, next);
        },
      }
    : form;
  const move = (index: number, offset: number) => {
    const next = items.slice();
    const [item] = next.splice(index, 1);
    next.splice(index + offset, 0, item);
    update(next);
  };

  return (
    <Box>
      <FieldLabel label={label} required={required} aside={aside} />
      {help && <Typography sx={{ fontSize: 12, color: color.gray500 }}>{help}</Typography>}
      {field.ai_fills_if_empty && (
        <Typography sx={{ fontSize: 12, color: color.gray500 }}>
          <AiHint />
        </Typography>
      )}
      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {items.map((item, index) => {
          const itemPath = joinPath(path, index);
          const itemError = errorText(form.errors[itemPath]);
          return (
            <Box
              key={index}
              id={fieldDomId(itemPath)}
              sx={{
                p: field.item_fields?.length ? 1.5 : 1,
                borderRadius: "10px",
                border: `1px solid ${color.gray200}`,
                backgroundColor: color.gray50,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: field.item_fields?.length ? 1.5 : 0 }}>
                {field.item_fields?.length ? (
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: color.gray700, flex: 1 }}>
                    {t("create.form.item", { number: index + 1 })}
                  </Typography>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    value={typeof item === "string" ? item : ""}
                    placeholder={placeholder}
                    disabled={form.disabled}
                    onChange={(event) => form.onChange(itemPath, event.target.value)}
                    inputProps={{
                      maxLength: field.max_length ?? undefined,
                      "aria-label": t("create.form.itemOf", { label, number: index + 1 }),
                    }}
                    error={!!itemError}
                    helperText={itemError}
                    sx={{ backgroundColor: color.white }}
                  />
                )}
                <Tooltip title={t("create.form.moveUp")} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={form.disabled || index === 0}
                      onClick={() => move(index, -1)}
                      aria-label={t("create.form.moveUp")}
                    >
                      <ArrowUpwardRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("create.form.moveDown")} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={form.disabled || index === items.length - 1}
                      onClick={() => move(index, 1)}
                      aria-label={t("create.form.moveDown")}
                    >
                      <ArrowDownwardRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t("create.form.removeItem")} arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={form.disabled || items.length <= keepRows}
                      onClick={() => update(items.filter((_, position) => position !== index))}
                      aria-label={t("create.form.removeItemNumber", { number: index + 1 })}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              {itemFields.length ? (
                <SchemaFormContext.Provider value={rowContext}>
                  <FieldGrid fields={itemFields} prefix={itemPath} scope={asRecord(item)} />
                </SchemaFormContext.Provider>
              ) : null}
            </Box>
          );
        })}
        {!items.length && (
          <Typography sx={{ fontSize: 13, color: color.gray500 }}>{t("create.form.listEmpty")}</Typography>
        )}
      </Box>
      <Button
        size="small"
        color="secondary"
        startIcon={<AddRounded />}
        disabled={!canAdd}
        onClick={() => update([...items, emptyListItem(field)])}
        sx={{ mt: 1 }}
      >
        {t("create.form.addItem")}
      </Button>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

const GroupField: React.FC<{
  field: DocumentCraftingFormField;
  path: string;
  value: unknown;
  required: boolean;
}> = ({ field, path, value, required }) => {
  const { t, i18n } = useTranslation("documentCrafting");
  const form = useSchemaForm();
  const errorText = useFormErrorText();
  const group = asRecord(value);
  const label = localizedText(field.label, i18n.language) || field.key;
  const help = localizedText(field.help, i18n.language);
  const error = errorText(form.errors[path]);
  const children = field.fields ?? [];
  // SPEC §5.1: without name and phone the contact block falls back to the
  // company; allowed, but not recommended.
  const contactFallback =
    field.key === "contact" &&
    children.some((child) => child.key === "name") &&
    children.some((child) => child.key === "phone") &&
    isEmptyValue(group.name) &&
    isEmptyValue(group.phone);

  return (
    <Box sx={{ pt: 1.5, borderTop: `1px solid ${color.gray100}` }}>
      <FieldLabel
        label={label}
        required={required}
        aside={required ? undefined : t("create.form.optionalGroup")}
      />
      {help && <Typography sx={{ fontSize: 12, color: color.gray500 }}>{help}</Typography>}
      <Box sx={{ mt: 1.5 }}>
        <FieldGrid fields={children} prefix={path} scope={group} />
      </Box>
      {contactFallback && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {t("create.form.contactFallback")}
        </Alert>
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

/**
 * Generic renderer of the backend form schema (CONTRACTS §6.5): text,
 * textarea with counter, number, date, time, url, email, select (optionally
 * with free text), multiselect, checkbox, lists of rows with add / remove /
 * reorder within min and max, groups and file uploads. Required flags,
 * including `required_if`, follow the current values.
 */
const SchemaForm: React.FC<SchemaFormProps> = ({ fields, schema, ...context }) => (
  <SchemaFormContext.Provider value={{ ...context, schema: schema ?? fields }}>
    <FieldGrid fields={fields} prefix="" scope={context.values} />
  </SchemaFormContext.Provider>
);

export default SchemaForm;
